<template>
  <nav class="account-sidebar" :class="{ 'account-sidebar--open': mobileOpen }">
    <button class="account-sidebar__mobile-toggle d-lg-none" @click="mobileOpen = !mobileOpen">
      <i :class="mobileOpen ? 'fe-x' : 'fe-menu'" />
      <span>{{ activeItemText }}</span>
    </button>

    <div class="account-sidebar__nav">
      <router-link
        v-for="item in navigationItems"
        :key="item.name"
        :to="{ name: item.name }"
        :class="['account-sidebar__item', { 'account-sidebar__item--active': activeName === item.name }]"
        :aria-current="activeName === item.name ? 'page' : null"
      >
        <i :class="item.icon" />
        <span>{{ item.text }}</span>
        <span v-if="item.isNew" class="account-sidebar__badge account-sidebar__badge--success">New</span>
        <span v-if="item.isBeta" class="account-sidebar__badge account-sidebar__badge--primary">Beta</span>
        <span v-if="item.comingSoon" class="account-sidebar__badge account-sidebar__badge--muted">Soon</span>
      </router-link>

      <div class="account-sidebar__serial">
        {{ $t('pages.profile.sidebar.serial') }}:
        <strong>{{ licenseMapping[user.email.toLowerCase()] || user.id }}</strong>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState } from "vuex";

export default {
  data() {
    return {
      mobileOpen: false,
      licenseMapping: {
        "helensdiet@windowslive.com": "528f263e-edee-4ef1-bd0b-563492e7a21",
        "info@katakisdimitris.gr": "51ew85fr-2939-47fd-a7dd-ab96dn6d26p5",
        "tounda_dimi@yahoo.gr": "51ec35cc-2939-47fd-a1dd-ab69dd69dd6d26c5",
        "nutrition@eurodiet.gr": "67a65311-b6a4-4cdb-aad6-497f491f1d9b",
        "mixalangelos@gmail.com": "52ec35cc-2938-47fd-a1dd-ab69dd6d26c6",
      },
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.currentUser,
    }),

    currUrl() {
      return this.$route.name;
    },

    activeName() {
      return this.$route.meta.parentRoute || this.$route.name;
    },

    activeItemText() {
      const active = this.navigationItems.find(item => item.name === this.activeName);
      return active ? active.text : this.$t('pages.profile.account');
    },

    navigationItems() {
      return [
        {
          name: "Profile",
          icon: "icon-user",
          text: this.$t("pages.profile.sidebar.dashboard"),
        },
        ...(!this.isOfflineVersion
          ? [{ name: "company", icon: "icon-globe", text: this.$t("pages.profile.sidebar.company") }]
          : []),
        {
          name: "change-password",
          icon: "icon-lock",
          text: this.$t("pages.profile.sidebar.password"),
        },
        ...(!this.isOfflineVersion
          ? [{ name: "passcode-settings", icon: "icon-shield", text: this.$t("pages.passcode.sidebar") }]
          : []),
        {
          name: "user-todo",
          icon: "icon-check",
          text: this.$t("pages.profile.sidebar.todo"),
        },
        {
          name: "user-tags",
          icon: "icon-tag",
          text: this.$t("pages.profile.sidebar.tags"),
        },
        {
          name: "connected-agents",
          icon: "icon-puzzle",
          text: this.$t("pages.connectedAgents.sidebar"),
          isNew: true,
        },
        ...(!this.isOfflineVersion
          ? [{ name: "user-subscription", icon: "icon-refresh", text: this.$t("pages.profile.sidebar.subscription") }]
          : []),
        ...(!this.isOfflineVersion
          ? [{ name: "sms", icon: "icon-screen-smartphone", text: this.$t("pages.profile.sidebar.sms") }]
          : []),
        {
          name: "print-templates",
          icon: "icon-printer",
          text: this.$t("pages.profile.sidebar.printTemplates"),
        },
        {
          name: "note-templates",
          icon: "icon-notebook",
          text: this.$t("pages.profile.sidebar.noteTemplates"),
        },
        {
          name: "questionnaires",
          icon: "icon-question",
          text: this.$t("pages.profile.sidebar.questionnaires"),
        },
      ];
    },
  },

  watch: {
    '$route'() {
      this.mobileOpen = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.account-sidebar {
  flex: 0 0 330px;
  background: #fff;
  border: 1px solid $color_border;
  border-radius: 12px;
  padding: 1rem 0;
  position: sticky;
  top: calc(#{$topbar-height} + 1.25rem);
  align-self: flex-start;

  @media (max-width: 991px) {
    flex: none;
    width: 100%;
    position: relative;
    top: 0;
    padding: 0;
    border-radius: 12px;
  }
}

.account-sidebar__mobile-toggle {
  display: none;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  font-weight: 600;
  color: $color_text;
  cursor: pointer;

  i {
    margin-right: 0.5rem;
  }

  @media (max-width: 991px) {
    display: flex;
    align-items: center;
  }
}

.account-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 0.6rem;

  @media (max-width: 991px) {
    display: none;
    padding: 0.5rem 0.75rem 0.75rem;

    .account-sidebar--open & {
      display: flex;
    }
  }
}

.account-sidebar__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 500;
  color: $color_text;
  cursor: pointer;
  text-decoration: none;
  border-radius: 10px;
  transition: all 0.15s ease;

  i {
    font-size: 0.95rem;
    width: 18px;
    text-align: center;
    flex-shrink: 0;
    opacity: 0.65;
  }

  span {
    line-height: 1.3;
  }

  &:hover {
    background: rgba($color_primary, 0.06);
    color: $color_primary;
    text-decoration: none;

    i { opacity: 1; }
  }

  &--active {
    border-left: 2px solid $color_primary;
    color: $color_primary;
    background: rgba($color_primary, 0.08);
    font-weight: 600;

    i { opacity: 1; }
  }
}

.account-sidebar__badge {
  font-size: 0.6rem;
  font-weight: 600;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  line-height: 1.4;
  flex-shrink: 0;

  &--success {
    background: #dcfce7;
    color: #166534;
  }

  &--primary {
    background: rgba($color_primary, 0.1);
    color: $color_primary;
  }

  &--muted {
    background: #f3f4f6;
    color: #6b7280;
  }
}

.account-sidebar__serial {
  margin-top: auto;
  padding: 1rem 0.6rem 0;
  font-size: 0.7rem;
  color: $color_primary_muted;
  word-break: break-all;

  strong {
    display: block;
    margin-top: 2px;
    font-size: 0.65rem;
  }
}
</style>
